import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';

const NOTICES_API = 'https://system.sales-management.cloud/api/notices.php';

const STATUS_OPTIONS = [
  { value: '', label: '全て' },
  { value: 'sent', label: '送信成功' },
  { value: 'failed', label: '送信失敗' },
  { value: 'no_subscription', label: '購読なし' },
];

const DELIVERY_TYPE_OPTIONS = [
  { value: '', label: '全て' },
  { value: 'user', label: '個別配信' },
  { value: 'department', label: '部署配信' },
];

function PushDeliveryLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [deliveryType, setDeliveryType] = useState('');
  const [limit, setLimit] = useState(100);

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      scope: 'push_logs',
      limit: String(limit),
    });

    if (status) {
      params.set('status', status);
    }
    if (deliveryType) {
      params.set('delivery_type', deliveryType);
    }

    return params.toString();
  }, [status, deliveryType, limit]);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        setLogs([]);
        return;
      }

      const response = await fetch(`${NOTICES_API}?${queryString}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (result.status === 'success') {
        setLogs(result.data?.logs || []);
      } else {
        setLogs([]);
        setError(result.message || 'プッシュ配信ログの取得に失敗しました');
      }
    } catch (err) {
      console.error('Failed to fetch push delivery logs:', err);
      setLogs([]);
      setError('プッシュ配信ログの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">プッシュ配信ログ</h1>
        <p className="text-sm text-gray-600 mt-2">誰が誰にどの内容をどの端末へ送ったかを確認できます。</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">送信結果</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">配信種別</label>
            <select
              value={deliveryType}
              onChange={(e) => setDeliveryType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              {DELIVERY_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">表示件数</label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value={50}>50件</option>
              <option value={100}>100件</option>
              <option value={200}>200件</option>
              <option value={500}>500件</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="button"
              onClick={loadLogs}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              再読み込み
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">読み込み中...</p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-gray-500">該当するログがありません</p>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <StatusBadge status={log.send_status} />
                  <TypeBadge deliveryType={log.delivery_type} pushOnly={log.push_only} />
                  <span className="text-xs text-gray-600">送信時刻: {formatDateTime(log.created_at)}</span>
                  {log.http_status !== null && (
                    <span className="text-xs text-gray-600">HTTP: {log.http_status}</span>
                  )}
                </div>

                <p className="text-sm text-gray-900 font-semibold">{log.title}</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{log.message}</p>

                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-600">
                  <p>送信者: {formatUser(log.sender_user_name, log.sender_user_id)}</p>
                  <p>受信者: {formatUser(log.recipient_user_name, log.recipient_user_id)}</p>
                  <p>通知ID: {log.notice_id ?? '-'}</p>
                  <p>対象部署: {log.target_department || '-'}</p>
                  <p>遷移先URL: {log.target_url || '-'}</p>
                  <p>Push先ホスト: {log.endpoint_host || '-'}</p>
                  <p>端末種別: {log.device_type || '-'}</p>
                  <p>プラットフォーム: {log.device_platform || '-'}</p>
                  <p>言語: {log.device_language || '-'}</p>
                  <p>アプリエージェント: {log.app_agent || '-'}</p>
                </div>

                {log.user_agent && (
                  <p className="text-[11px] text-gray-500 mt-2 break-all">UA: {log.user_agent}</p>
                )}
                {log.error_message && (
                  <p className="text-[11px] text-red-600 mt-2 break-all">Error: {log.error_message}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === 'sent') {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">送信成功</span>;
  }
  if (status === 'no_subscription') {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">購読なし</span>;
  }
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">送信失敗</span>;
}

function TypeBadge({ deliveryType, pushOnly }) {
  if (pushOnly) {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">プッシュのみ</span>;
  }
  if (deliveryType === 'department') {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">部署配信</span>;
  }
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">個別配信</span>;
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  return new Date(value).toLocaleString('ja-JP');
}

function formatUser(name, id) {
  if (name) {
    return `${name} (ID: ${id ?? '-'})`;
  }
  return id ? `ID: ${id}` : '-';
}

export default PushDeliveryLogs;
